import { CircularProgress, InputAdornment, makeStyles, TextField, TextFieldProps } from '@material-ui/core';
import Autocomplete, { AutocompleteProps } from '@material-ui/lab/Autocomplete';
import { FilterOptionsState } from '@material-ui/lab/useAutocomplete';
import React, { useMemo } from 'react';
import LinesEllipsis from 'react-lines-ellipsis';

import { ReactComponent as SearchSvg } from '../../../../../assets/search_2.svg';
import { SearchFilters, SearchSortOrder } from '../../../../../graphql/gen/types';
import { usePlexes, usePlexesMap } from '../../../../../models/filters/useSelectedPlexIds';
import { colorPalette, spacings, TextSizesEnum } from '../../../../../theme';
import { notEmpty } from '../../../../../utils/notEmpty';
import { PlexTypography } from '../../../../shared/primitives/PlexTypography';
import { PlexName } from '../../../common/PlexName';
import { ALL_DOCUMENT_TYPES, SearchResultOption, SearchState, useSearchAdvanced } from '../../../search/useSearch';

type SearchAutocompleteProps<T> = {
  onSelect: (value: T) => void;
  mapSearchResult: (searchOption: SearchResultOption) => T | null;
  filters: Partial<SearchFilters>;
  getPlexId: (value: T) => string | null | undefined;
  getTitle: (value: T) => string;
  getBody: (value: T) => string;
  filterOptions?: (options: T[], params: FilterOptionsState<T>) => T[];
  autocompleteProps?: Partial<AutocompleteProps<T, false, false, false>>;
};

export function SearchAutocomplete<Option>({
  onSelect,
  mapSearchResult,
  getPlexId,
  getTitle,
  getBody,
  filters,
  filterOptions = (options) => options,
  autocompleteProps,
}: SearchAutocompleteProps<Option>): React.ReactElement {
  const classes = useStyles();
  const plexes = usePlexes();

  const options = useMemo(
    () => ({
      sortOrder: SearchSortOrder.UpdatedAt,
      filters: {
        plexIds: plexes.map((plex) => plex.id),
        types: ALL_DOCUMENT_TYPES,
        labelIds: [],
        ...filters,
      },
      minQueryTextLength: 3,
    }),
    [plexes, filters]
  );

  const [search, setSearchQuery, searchQuery] = useSearchAdvanced(null, options);
  const plexesMap = usePlexesMap();

  const handleQueryChange = (_: React.ChangeEvent<unknown>, value: string): void => {
    setSearchQuery({ input: value });
  };

  const handleChange = (_: React.ChangeEvent<unknown>, value: Option | null): void => {
    if (value != null) {
      onSelect(value);
      setSearchQuery({ input: '' });
    }
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      clearOnEscape
      noOptionsText={getNoOptionsText(search)}
      style={{ width: '100%' }}
      classes={{ inputRoot: classes.searchInput }}
      onChange={handleChange}
      value={null}
      getOptionSelected={(option: Option, value: Option): boolean => {
        return JSON.stringify(option) === JSON.stringify(value);
      }}
      getOptionLabel={(): string => ''}
      loading={isLoading(search)}
      options={search.state === 'done' ? search.options.map(mapSearchResult).filter(notEmpty) : []}
      filterOptions={filterOptions}
      renderOption={(option: Option): React.ReactNode => {
        const plexId = getPlexId(option);

        return (
          <div className={classes.autocompleteItem}>
            {plexId != null && plexesMap[plexId] != null && (
              <div className={classes.bubbleRow}>
                <PlexName
                  name={plexesMap[plexId].name}
                  color={plexesMap[plexId].color}
                  className={classes.plexBubble}
                />
              </div>
            )}
            <PlexTypography className={classes.factTitle} text={getTitle(option)} size={TextSizesEnum.Normal} />
            <PlexTypography className={classes.factBody} size={TextSizesEnum.Small}>
              <LinesEllipsis text={getBody(option)} maxLine={2} />
            </PlexTypography>
          </div>
        );
      }}
      onInputChange={handleQueryChange}
      inputValue={searchQuery.input}
      renderInput={(params: TextFieldProps): React.ReactElement => (
        <TextField
          {...params}
          placeholder={`Search resource`}
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading(search) ? <CircularProgress color="inherit" size={20} /> : null}
              </React.Fragment>
            ),
            startAdornment: (
              <InputAdornment position="start">
                <SearchSvg />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
}

const useStyles = makeStyles((theme) => ({
  autocompleteItemContainer: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    flexDirection: 'row',
    paddingInline: spacings.xsmall,
  },
  autocompleteItemLabel: {
    display: 'flex',
    flex: 1,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
  autocompleteItem: {},
  bubbleRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  plexBubble: {
    marginRight: spacings.small,
  },
  factTitle: {
    color: '#464663',
  },
  factBody: {
    color: colorPalette.purpleGray._500,
  },
  searchInput: {
    display: 'flex',
    paddingLeft: spacings.xlarge,
  },
}));

function isLoading(search: SearchState): boolean {
  return search.state === 'can_load' || search.state === 'loading';
}

function getNoOptionsText(search: SearchState): string | null {
  switch (search.state) {
    case 'initial':
      return 'Type at least 3 characters to search';
    case 'more_input_needed':
      return 'Type at least 3 characters to search';
    case 'done':
      return 'No results match your query';
    default:
      return null;
  }
}
